import { CheckCircleIcon, CloseCircleIcon, InfoIcon, XCircleIcon } from '@systemeio/ui/icons'
import { ActionMenu, ActionMenuItem, ActionMenuItemProps } from '@systemeio/ui'
import ItemCount from 'shared/components/item-count'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twMerge } from 'tailwind-merge'
import { getNotificationIcon, ruleIconSizeClassName } from '../constants/notification-icons'
import { useNotificationsActions } from '../hooks/use-notification-actions'
import {
  NotificationBaseItemInterface,
  NotificationItemInterface,
  NotificationSharingItemInterface,
} from '../types/notification-types'
import { NotificationTypeEnum } from 'modules/profile/notification-settings/enums/notification-settings-enum'
import { SharingStatusEnum } from 'modules/share/enum/sharing-status-enum'
import { ReactNode } from 'react'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { SharingFailReasonEnum } from 'modules/share/enum/sharing-fail-reason-enum'
import { useSharingStatusPolling } from 'modules/share/hooks/use-sharing-status-polling'
import { useCancelSharing } from 'modules/share/hooks/use-cancel-sharing'
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url'

export interface NotificationItemViewProps {
  isRead?: boolean
  icon?: ReactNode
  message: string
  description?: ReactNode
  showCancelButton?: boolean
  isCancelling?: boolean
  actions?: ActionMenuItemProps[]
  onClick?: () => void
  onCancel?: () => void
}

export const NotificationItemView = ({
  isRead,
  icon,
  message,
  description,
  showCancelButton,
  isCancelling,
  actions,
  onClick,
  onCancel,
}: NotificationItemViewProps) => {
  const { t } = useLocoTranslation()

  const isActions = actions && actions.length > 0

  return (
    <div
      className={twMerge(
        'bg-background-secondary border-text-secondary text-text-primary hover:bg-brand-primary-subtle group flex flex-row justify-between border-b px-6 py-4 transition last:border-b-0',
        isRead && 'text-text-secondary group-hover:text-text-primary',
        onClick && 'cursor-pointer',
      )}
      onClick={onClick}
    >
      <div className="inline-flex w-full flex-row items-center gap-4">
        {icon}

        <div className="text-start mr-auto w-full">
          <div>
            <span
              className="group-hover:text-text-primary transition"
              dangerouslySetInnerHTML={{ __html: message }}
            />
          </div>
          {description}
        </div>

        {showCancelButton && (
          <button
            className={twMerge('group/cancel', isCancelling && 'animate-pulse cursor-progress')}
            disabled={isCancelling}
            onClick={e => {
              e.stopPropagation()
              onCancel?.()
            }}
          >
            <XCircleIcon className="group-hover/cancel:[&>path]:fill-brand-primary [&>path]:main-transition-colors h-5 w-5" />
          </button>
        )}
      </div>
      {(!isRead || isActions) && (
        <div className="inline-flex flex-row items-center gap-4 pl-3">
          {!isRead && <ItemCount>{t('dashboard.notifications.new')}</ItemCount>}

          {isActions && (
            <ActionMenu
              menuItems={actions.map(action => (
                <ActionMenuItem
                  key={`${action.label}-na-action`}
                  label={action.label}
                  onClick={action.onClick}
                />
              ))}
            />
          )}
        </div>
      )}
    </div>
  )
}

export interface SharingNotificationProps {
  notification: NotificationSharingItemInterface
}

const SharingNotificationItem = ({ notification: notificationItem }: SharingNotificationProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const { cancelSharing, isCancelling } = useCancelSharing()

  const { sharingStatus, mutate } = useSharingStatusPolling({
    sharingId: notificationItem?.data.sharingId,
  })

  const notification = {
    ...notificationItem,
    data: {
      ...notificationItem.data,
      ...(sharingStatus ?? {}),
    },
  }

  const sharedResourceId = notification.data.sharedResourceId

  const handleNotificationClick =
    notification.data.status === SharingStatusEnum.Success && sharedResourceId
      ? async () => {
          window.open(getWorkflowEditorUrl(sharedResourceId), '_blank')
        }
      : undefined

  const handleCancel = async () => {
    await cancelSharing(notification.data.sharingId)

    await mutate(data => {
      if (!data) return

      return {
        ...data,
        status: SharingStatusEnum.Failed,
        failReason: SharingFailReasonEnum.CancelledByUser,
      }
    }, false)
  }

  const getIcon = () => {
    switch (notification.data.status) {
      case SharingStatusEnum.Running:
        return <InfoIcon className={twMerge(ruleIconSizeClassName, 'fill-yellow')} />
      case SharingStatusEnum.Failed:
        if (notification.data.failReason === SharingFailReasonEnum.CancelledByUser) {
          return <CloseCircleIcon className={ruleIconSizeClassName} />
        } else {
          return <CloseCircleIcon className={twMerge(ruleIconSizeClassName, 'fill-red')} />
        }
      case SharingStatusEnum.Success:
        return <CheckCircleIcon className={twMerge(ruleIconSizeClassName, 'fill-green')} />
      default:
        return <InfoIcon className={twMerge(ruleIconSizeClassName, 'fill-yellow')} />
    }
  }

  const getMessage = () => {
    switch (notification.data.status) {
      case SharingStatusEnum.Running:
        return t('dashboard.notifications.workflow_sharing_in_progress')
      case SharingStatusEnum.Failed:
        switch (notification.data.failReason) {
          case SharingFailReasonEnum.CancelledByUser:
            return t('dashboard.notifications.workflow_sharing_cancelled')
          case SharingFailReasonEnum.FailedToShare:
            return t('dashboard.notifications.workflow_sharing_failed_to_share')
          case SharingFailReasonEnum.ProcessingTimeExceeded:
            return t('dashboard.notifications.workflow_sharing_processing_time_exceeded')
          case SharingFailReasonEnum.ResourceRemoved:
            return t('dashboard.notifications.workflow_sharing_resource_removed')
          default:
            return t('dashboard.notifications.workflow_sharing_failed')
        }
      case SharingStatusEnum.Success:
        return t('dashboard.notifications.workflow_sharing_completed')
    }
  }

  const getDescription = () => {
    switch (notification.data.status) {
      case SharingStatusEnum.Running:
        return (
          notification.data?.progressPercent !== undefined && (
            <div className="flex items-center gap-3">
              <div className="bg-background-tertiary relative h-1 w-full max-w-[342px] rounded-full">
                <div
                  className="bg-brand-primary absolute left-0 top-0 h-full rounded-full transition-[width] duration-300 ease-in-out"
                  style={{ width: `${notification.data.progressPercent}%` }}
                ></div>
              </div>

              <div className="text-text-primary text-xs font-medium">
                {notification.data.progressPercent}%
              </div>
            </div>
          )
        )
      case SharingStatusEnum.Failed:
      case SharingStatusEnum.Success:
        return (
          user && (
            <div className="group-hover:text-text-primary transition">
              {dateTimeFormat(notification.data.updatedAt, {
                timeFormat: user.timeFormat,
                dateFormat: user.dateFormat,
                timeZone: user.timezone,
              })}
            </div>
          )
        )
    }
  }

  return (
    <NotificationItemView
      isRead={notification.read}
      icon={getIcon()}
      message={getMessage()}
      description={getDescription()}
      showCancelButton={notification.data.status === SharingStatusEnum.Running}
      isCancelling={isCancelling}
      onClick={handleNotificationClick}
      onCancel={handleCancel}
    />
  )
}

export interface BaseNotificationProps {
  notification: NotificationBaseItemInterface
  actions?: ActionMenuItemProps[]
}

const BaseNotificationItem = ({ notification }: BaseNotificationProps) => {
  const { user } = useUser()
  const { getNotificationLink } = useNotificationsActions()

  const handleNotificationClick = async () => {
    const { link } = await getNotificationLink(notification.id)
    window.open(link, '_blank')
  }

  return (
    <NotificationItemView
      isRead={notification.read}
      icon={getNotificationIcon(notification)}
      message={notification.message}
      description={
        user && (
          <div className="group-hover:text-text-primary transition">
            {dateTimeFormat(notification.createdAt, {
              timeFormat: user.timeFormat,
              dateFormat: user.dateFormat,
              timeZone: user.timezone,
            })}
          </div>
        )
      }
      onClick={handleNotificationClick}
    />
  )
}

export interface NotificationItemProps {
  notification: NotificationItemInterface
  actions?: ActionMenuItemProps[]
}

export const NotificationItem = ({ notification, actions }: NotificationItemProps) => {
  switch (notification.eventType) {
    case NotificationTypeEnum.SharingStarted: {
      return <SharingNotificationItem notification={notification} />
    }
    default: {
      return <BaseNotificationItem notification={notification} actions={actions} />
    }
  }
}
